import { PrinterBusyError } from './printer-busy.error';
import { PrinterNotReadyError } from './printer-not-ready.error';
import { PrinterRetryableError } from './printer-retryable.error';
import { PrinterUncertainError } from './printer-uncertain.error';
import { isStarConfirmedNotReadyReason, STAR_CONFIRMED_NOT_READY_REASONS } from './star-print-outcome';

export const PRINT_FAILURE_REASON_BUSY = 'PRINTER_BUSY';
export const PRINT_FAILURE_REASON_UNCERTAIN = 'UNCERTAIN';

const REASON_TRANSLATION_KEYS: Readonly<Record<string, string>> = {
  PAPER_EMPTY: 'printing_queue_reason_paper_empty',
  COVER_OPEN: 'printing_queue_reason_cover_open',
  PAPER_JAM: 'printing_queue_reason_paper_jam',
  HEAD_UP: 'printing_queue_reason_head_up',
  CUTTER_ERROR: 'printing_queue_reason_cutter_error',
  OVER_TEMP: 'printing_queue_reason_over_temp',
  HARDWARE: 'printing_queue_reason_hardware',
  PAPER_NOT_TAKEN: 'printing_queue_reason_paper_not_taken',
  OFFLINE_OTHER: 'printing_queue_reason_not_ready',
  NO_CONNECTION: 'printing_queue_reason_no_connection',
  WRITE_FAILED: 'printing_queue_reason_no_connection',
  [PRINT_FAILURE_REASON_BUSY]: 'printing_queue_reason_busy',
};

/** Reason shown for a failed print attempt; undefined for errors without a classified reason. */
export function printFailureReasonOf(error: unknown): string | undefined {
  if (error instanceof PrinterNotReadyError || error instanceof PrinterRetryableError) {
    return error.reason;
  }
  if (error instanceof PrinterUncertainError) {
    return error.reason ?? PRINT_FAILURE_REASON_UNCERTAIN;
  }
  if (error instanceof PrinterBusyError) {
    return PRINT_FAILURE_REASON_BUSY;
  }
  return undefined;
}

/**
 * Translation keys describing a failed attempt. Failures after the data was sent add the
 * "may have printed" text, because the operator must check the printer before repeating.
 */
export function printFailureReasonTranslationKeys(reason: string | undefined, mayHavePrinted: boolean): string[] {
  const keys: string[] = [];
  const reasonKey = reason ? REASON_TRANSLATION_KEYS[reason] : undefined;
  if (reasonKey) {
    keys.push(reasonKey);
  }
  if (mayHavePrinted) {
    keys.push('printing_queue_reason_uncertain');
  } else if (reason && !reasonKey) {
    keys.push('printing_queue_reason_not_ready');
  }
  return keys;
}

/**
 * Whether a queue item's failure should turn the printer status icon red. Busy is transient
 * contention and stays neutral; a confirmed paper/cover problem is shown at once, also while the
 * item is still being retried, other reasons once the item has failed.
 */
export function printFailureNeedsAttention(reason: string | undefined, isFailed: boolean): boolean {
  if (!reason || reason === PRINT_FAILURE_REASON_BUSY) {
    return false;
  }
  return isFailed || STAR_CONFIRMED_NOT_READY_REASONS.includes(reason);
}

/**
 * True for failures that the Star plugin before 7.3.2 reported as a successful print on iOS.
 * Callers use it to keep the side effects of that old "success" (e.g. continuing the kitchen
 * receipt chain of a self-order), so a rare printer problem does not change the rest of the flow.
 */
export function isLegacyIosSuccess(error: unknown): boolean {
  return (
    (error instanceof PrinterNotReadyError || error instanceof PrinterRetryableError || error instanceof PrinterUncertainError) &&
    error.legacyIosSuccess === true
  );
}

export function isPaperOrCoverWait(error: unknown): boolean {
  return error instanceof PrinterRetryableError && isStarConfirmedNotReadyReason(error.reason);
}

/** True while a queue item that failed for no paper / an open cover waits to be printed again. */
export function printFailureWaitsForPrinter(reason: string | undefined, isFailed: boolean): boolean {
  return !isFailed && isStarConfirmedNotReadyReason(reason);
}

/**
 * True when a failed attempt must not stop what follows a printed receipt: the old iOS plugin
 * reported it as success, the data was already sent to the printer (post-write uncertainty,
 * which the old app retried until it printed), or the job waits in the queue for paper / a closed
 * cover and prints by itself.
 */
export function continuesAsPrinted(error: unknown): boolean {
  return isLegacyIosSuccess(error) || error instanceof PrinterUncertainError || isPaperOrCoverWait(error);
}
